import { N8nModule } from '../../types';

export const module3: N8nModule = {
  id: 'module3',
  title: 'Building Practical Workflows',
  description: 'Master API integrations, database operations, communication workflows, and file handling to build real-world automation solutions.',
  duration: '2.5 hours',
  objectives: [
    'Integrate with REST and GraphQL APIs',
    'Connect to databases (PostgreSQL, MySQL, MongoDB)',
    'Automate email and messaging workflows',
    'Handle file operations and cloud storage',
    'Implement authentication patterns',
    'Manage rate limiting and pagination'
  ],
  content: `# Building Practical Workflows

## Learning Objectives
- Integrate with popular APIs (REST, GraphQL)
- Connect to databases (PostgreSQL, MySQL, MongoDB)
- Automate email and messaging workflows
- Handle file operations and cloud storage

## API Integration Patterns

### REST API Workflows
n8n excels at consuming REST APIs. The HTTP Request node supports all HTTP methods and authentication types.

**Key Concepts:**
- **GET requests**: Fetch data from external services
- **POST requests**: Create new resources
- **PUT/PATCH**: Update existing data
- **DELETE**: Remove resources
- **Query parameters**: Filter and paginate results
- **Headers**: Set authentication tokens and content types

### GraphQL Queries
GraphQL provides a more flexible alternative to REST, allowing clients to request exactly the data they need.

**Benefits:**
- Single endpoint for all operations
- Strongly typed schema
- Reduced over-fetching
- Batched requests

### Authentication Methods
n8n supports multiple authentication patterns:

1. **API Key**: Simple header or query parameter
2. **OAuth 2.0**: Industry standard for third-party access
3. **Bearer Token**: JWT-based authentication
4. **Basic Auth**: Username and password
5. **Custom Auth**: Flexible for proprietary systems

### Rate Limiting and Pagination
Most APIs have rate limits and pagination to prevent abuse and manage large datasets.

**Strategies:**
- **Throttling**: Delay between requests
- **Pagination loops**: Iterate through pages
- **Cursor-based pagination**: More efficient for large datasets
- **Retry logic**: Handle temporary failures

## Database Operations

### PostgreSQL CRUD Operations
PostgreSQL is a powerful relational database with excellent JSON support.

**Common Operations:**
- **INSERT**: Add new records
- **SELECT**: Query data with filters
- **UPDATE**: Modify existing records
- **DELETE**: Remove records
- **JOIN**: Combine data from multiple tables
- **JSONB**: Store and query JSON data

### MySQL Queries
MySQL is widely used for web applications and offers high performance.

**Best Practices:**
- Use prepared statements to prevent SQL injection
- Index frequently queried columns
- Optimize JOIN operations
- Monitor query performance

### MongoDB Aggregations
MongoDB is a NoSQL database that stores data in flexible JSON-like documents.

**Aggregation Pipeline:**
- **\$match**: Filter documents
- **\$group**: Group by field and aggregate
- **\$sort**: Order results
- **\$project**: Shape output documents
- **\$lookup**: Join collections

### Connection Pooling
For high-volume workflows, connection pooling improves performance by reusing database connections.

## Communication Workflows

### Email Automation
Automate transactional emails, notifications, and marketing campaigns.

**Use Cases:**
- Order confirmations
- Password resets
- Weekly reports
- Alert notifications
- Newsletter campaigns

**Providers:**
- Gmail (OAuth)
- SendGrid (API key)
- AWS SES (credentials)
- Mailgun (API key)

### Slack Notifications
Send rich notifications to Slack channels for team collaboration.

**Features:**
- Text messages
- Formatted blocks
- Mentions and notifications
- Thread replies
- File attachments

### Discord Webhooks
Similar to Slack, Discord is popular for developer communities and gaming.

**Capabilities:**
- Embed messages with rich formatting
- Multiple embeds per message
- Mentions and role pings
- Attachment support

### SMS via Twilio
Send SMS notifications for critical alerts and 2FA codes.

**Best Practices:**
- Keep messages concise
- Include opt-out instructions
- Monitor delivery rates
- Handle international numbers

## File Operations

### Local File System
Read and write files on the n8n server for data processing and archival.

**Operations:**
- Read file contents
- Write data to files
- List directory contents
- Move and rename files
- Delete files

### AWS S3 Integration
S3 is the industry standard for cloud object storage.

**Common Workflows:**
- Upload files from webhooks
- Download files for processing
- List bucket contents
- Generate presigned URLs
- Set object metadata and permissions

### Google Drive Operations
Automate document management and collaboration.

**Features:**
- Upload files and folders
- Download documents
- Share with permissions
- Search by metadata
- Convert formats (Docs to PDF)

### File Format Conversions
Transform data between formats for compatibility.

**Common Conversions:**
- CSV to JSON
- Excel to CSV
- PDF to text
- Image format conversions
- XML to JSON

## Error Handling in Integrations

Always implement robust error handling for external services:

1. **Network failures**: Retry with exponential backoff
2. **Rate limits**: Implement throttling and queuing
3. **Invalid data**: Validate before sending
4. **Timeout errors**: Set appropriate timeout values
5. **Monitoring**: Log errors and send alerts

## Performance Optimization

**Best Practices:**
- Batch API requests when possible
- Cache frequently accessed data
- Use webhooks instead of polling
- Implement circuit breakers for failing services
- Monitor execution times and optimize slow workflows
`,
  videoUrl: 'https://www.youtube.com/watch?v=example3',
  codeExamples: [
    {
      title: 'REST API GET Request with Authentication',
      explanation: 'Fetch user data from an external API with Bearer token authentication',
      code: `{
  "method": "GET",
  "url": "https://api.example.com/v1/users",
  "authentication": "genericCredentialType",
  "genericAuthType": "httpHeaderAuth",
  "headers": {
    "Authorization": "Bearer {{$credentials.apiToken}}",
    "Content-Type": "application/json"
  },
  "options": {
    "timeout": 10000,
    "redirect": {
      "followRedirects": true
    }
  }
}`,
      language: 'json'
    },
    {
      title: 'GraphQL Query Workflow',
      explanation: 'Execute a GraphQL query to fetch repository data from GitHub',
      code: `{
  "method": "POST",
  "url": "https://api.github.com/graphql",
  "authentication": "headerAuth",
  "headers": {
    "Authorization": "Bearer {{$credentials.githubToken}}"
  },
  "body": {
    "query": "query { repository(owner: \\"octocat\\", name: \\"Hello-World\\") { name description stargazerCount } }"
  },
  "options": {
    "bodyContentType": "json"
  }
}`,
      language: 'json'
    },
    {
      title: 'PostgreSQL Insert Operation',
      explanation: 'Insert new user record into PostgreSQL database',
      code: `{
  "operation": "executeQuery",
  "query": "INSERT INTO users (email, name, created_at) VALUES ($1, $2, NOW()) RETURNING id",
  "parameters": {
    "bindings": [
      "={{$json.email}}",
      "={{$json.name}}"
    ]
  }
}`,
      language: 'json'
    },
    {
      title: 'MongoDB Aggregation Pipeline',
      explanation: 'Group orders by status and calculate totals',
      code: `{
  "operation": "aggregate",
  "collection": "orders",
  "pipeline": [
    {
      "\\$match": {
        "createdAt": {
          "\\$gte": "{{$json.startDate}}"
        }
      }
    },
    {
      "\\$group": {
        "_id": "\\$status",
        "count": { "\\$sum": 1 },
        "totalAmount": { "\\$sum": "\\$amount" }
      }
    },
    {
      "\\$sort": { "totalAmount": -1 }
    }
  ]
}`,
      language: 'json'
    },
    {
      title: 'SendGrid Email with Dynamic Content',
      explanation: 'Send personalized email using SendGrid with template variables',
      code: `{
  "fromEmail": "noreply@company.com",
  "fromName": "Company Name",
  "toEmail": "={{$json.customerEmail}}",
  "subject": "Welcome {{$json.customerName}}!",
  "text": "Thank you for signing up!",
  "html": "<h1>Welcome {{$json.customerName}}</h1><p>We're excited to have you on board.</p>",
  "dynamicTemplateData": {
    "customerName": "={{$json.customerName}}",
    "activationLink": "={{$json.activationUrl}}"
  }
}`,
      language: 'json'
    },
    {
      title: 'Slack Formatted Message with Blocks',
      explanation: 'Send rich Slack notification with formatted blocks',
      code: `{
  "channel": "#notifications",
  "blocksUi": {
    "blocks": [
      {
        "type": "header",
        "text": {
          "type": "plain_text",
          "text": "New Order Alert"
        }
      },
      {
        "type": "section",
        "fields": [
          {
            "type": "mrkdwn",
            "text": "*Order ID:*\\n{{$json.orderId}}"
          },
          {
            "type": "mrkdwn",
            "text": "*Amount:* Amount value: {{$json.total}}"
          }
        ]
      },
      {
        "type": "actions",
        "elements": [
          {
            "type": "button",
            "text": {
              "type": "plain_text",
              "text": "View Order"
            },
            "url": "https://dashboard.example.com/orders/{{$json.orderId}}"
          }
        ]
      }
    ]
  }
}`,
      language: 'json'
    },
    {
      title: 'AWS S3 File Upload',
      explanation: 'Upload file to S3 bucket with metadata',
      code: `{
  "operation": "upload",
  "bucketName": "my-app-uploads",
  "fileName": "={{$json.filename}}",
  "fileContent": "={{$binary.data}}",
  "additionalFields": {
    "acl": "private",
    "contentType": "={{$json.mimeType}}",
    "metadata": {
      "uploadedBy": "={{$json.userId}}",
      "uploadDate": "={{$now.toISOString()}}"
    }
  }
}`,
      language: 'json'
    },
    {
      title: 'Google Drive File Upload',
      explanation: 'Upload file to Google Drive folder',
      code: `{
  "operation": "upload",
  "name": "={{$json.fileName}}",
  "parents": ["1A2B3C4D5E6F7G8H9I0J"],
  "binaryData": true,
  "binaryPropertyName": "data",
  "options": {
    "mimeType": "={{$json.mimeType}}"
  }
}`,
      language: 'json'
    }
  ],
  practiceExercise: {
    title: 'Multi-Step Integration Workflow',
    description: 'Build a complete workflow that processes e-commerce orders: fetch order data from API, enrich with customer data from database, transform the data, and send notifications via email and Slack.',
    requirements: [
      'HTTP Request node to fetch order from API',
      'PostgreSQL node to lookup customer details',
      'Function node to transform and enrich data',
      'SendGrid node for email notification',
      'Slack node for team notification',
      'Error handling for each external service'
    ],
    starterCode: `{
  "nodes": [
    {
      "name": "Order Webhook",
      "type": "n8n-nodes-base.webhook",
      "position": [250, 300],
      "webhookId": "order-received"
    },
    {
      "name": "Fetch Order Details",
      "type": "n8n-nodes-base.httpRequest",
      "position": [450, 300]
      // TODO: Configure API endpoint
    },
    {
      "name": "Get Customer Info",
      "type": "n8n-nodes-base.postgres",
      "position": [650, 300]
      // TODO: Add database query
    },
    {
      "name": "Transform Data",
      "type": "n8n-nodes-base.function",
      "position": [850, 300]
      // TODO: Add transformation logic
    },
    {
      "name": "Send Email",
      "type": "n8n-nodes-base.sendGrid",
      "position": [1050, 250]
      // TODO: Configure email
    },
    {
      "name": "Notify Team",
      "type": "n8n-nodes-base.slack",
      "position": [1050, 350]
      // TODO: Configure Slack message
    }
  ]
}`,
    solution: `{
  "nodes": [
    {
      "name": "Order Webhook",
      "type": "n8n-nodes-base.webhook",
      "parameters": {
        "path": "order-received",
        "responseMode": "responseNode",
        "authentication": "headerAuth"
      }
    },
    {
      "name": "Fetch Order Details",
      "type": "n8n-nodes-base.httpRequest",
      "parameters": {
        "url": "https://api.shop.com/orders/={{$json.body.orderId}}",
        "authentication": "predefinedCredentialType",
        "nodeCredentialType": "shopApi"
      }
    },
    {
      "name": "Get Customer Info",
      "type": "n8n-nodes-base.postgres",
      "parameters": {
        "operation": "executeQuery",
        "query": "SELECT email, name, vip_status FROM customers WHERE id = $1",
        "parameters": {
          "bindings": ["={{$json.customerId}}"]
        }
      }
    },
    {
      "name": "Transform Data",
      "type": "n8n-nodes-base.function",
      "parameters": {
        "functionCode": "const order = \\$input.first().json; const customer = \\$('Get Customer Info').first().json; return { orderId: order.id, customerName: customer.name, customerEmail: customer.email, isVip: customer.vip_status, total: order.total, items: order.items, status: order.status };"
        }
      }
    },
    {
      "name": "Send Email",
      "type": "n8n-nodes-base.sendGrid",
      "parameters": {
        "fromEmail": "orders@shop.com",
        "toEmail": "={{$json.customerEmail}}",
        "subject": "Order Confirmation #{{$json.orderId}}",
        "html": "<h1>Thank you {{$json.customerName}}!</h1><p>Your order total: {{$json.total}}</p>"
      }
    },
    {
      "name": "Notify Team",
      "type": "n8n-nodes-base.slack",
      "parameters": {
        "channel": "#orders",
        "text": "New order #{{$json.orderId}} - Total: {{$json.total}} - VIP: {{$json.isVip}}"
      }
    }
  ]
}`,
    hints: [
      'Use the webhook body to extract the orderId',
      'PostgreSQL query needs the customer ID from the order data',
      'In the Function node, use $input.first() and $("Node Name").first() to access data'
    ]
  },
  quiz: {
    questions: [
      {
        id: 'q1-http-method',
        question: 'Which HTTP method should you use to create a new resource via REST API?',
      options: ['GET', 'POST', 'PUT', 'DELETE'],
      correctAnswer: 1,
      explanation: 'POST is the standard HTTP method for creating new resources. GET retrieves data, PUT updates existing resources, and DELETE removes resources.'
      },
      {
        id: 'q2-graphql-advantage',
        question: 'What is the main advantage of GraphQL over REST APIs?',
      options: [
        'GraphQL is faster than REST',
        'Clients can request exactly the data they need',
        'GraphQL does not require authentication',
        'GraphQL only works with NoSQL databases'
      ],
      correctAnswer: 1,
      explanation: 'GraphQL allows clients to specify exactly which fields they need, preventing over-fetching and under-fetching of data. This makes it more flexible than REST.'
      },
      {
        id: 'q3-oauth',
        question: 'Which authentication method is recommended for third-party API access?',
      options: ['API Key', 'Basic Auth', 'OAuth 2.0', 'No authentication'],
      correctAnswer: 2,
      explanation: 'OAuth 2.0 is the industry standard for secure third-party API access, providing token-based authentication without sharing passwords.'
      },
      {
        id: 'q4-postgresql-json',
        question: 'What PostgreSQL feature allows you to efficiently store and query JSON data?',
      options: ['TEXT columns', 'BLOB storage', 'JSONB data type', 'VARCHAR fields'],
      correctAnswer: 2,
      explanation: 'JSONB is a binary JSON data type in PostgreSQL that provides efficient storage and indexing for JSON documents.'
      },
      {
        id: 'q5-mongodb-match',
        question: 'In MongoDB aggregation, which stage is used to filter documents?',
      options: ['$filter', '$where', '$match', '$find'],
      correctAnswer: 2,
      explanation: '$match is the aggregation pipeline stage used to filter documents, similar to the WHERE clause in SQL.'
      },
      {
        id: 'q6-sql-injection',
        question: 'What is the recommended way to prevent SQL injection in n8n database queries?',
      options: [
        'String concatenation',
        'Parameterized queries with bindings',
        'No special handling needed',
        'URL encoding'
      ],
      correctAnswer: 1,
      explanation: 'Parameterized queries with bindings ensure that user input is properly escaped, preventing SQL injection attacks.'
      },
      {
        id: 'q7-aws-s3',
        question: 'Which AWS service is used for object storage in n8n workflows?',
      options: ['EC2', 'Lambda', 'S3', 'RDS'],
      correctAnswer: 2,
      explanation: 'Amazon S3 (Simple Storage Service) is the AWS object storage service used for storing files, images, and other data.'
      },
      {
        id: 'q8-rate-limits',
        question: 'What is the best practice for handling API rate limits in n8n?',
      options: [
        'Ignore rate limits',
        'Implement throttling and retry logic',
        'Use multiple API keys',
        'Switch to a different API'
      ],
      correctAnswer: 1,
      explanation: 'Implementing throttling (delays between requests) and retry logic with exponential backoff is the best way to handle API rate limits gracefully.'
      }
    ]
  }
};
